import React from 'react';
import { IconType } from '../icons';
import { BosDropDownOptions, cn, DropDownIconProps, PivotFieldProps, TMessageBarSeverity } from 'types';
import { InputField } from '../input-field';
import { Dropdown } from '../dropdown';
import { MessageBar } from '../message-bar';

const dropDownIconProps: DropDownIconProps = {
  iconDropdownDown: IconType.NewNextChevronDown,
  iconDropdownUp: IconType.NewNextChevronUp,
  iconHeight: 24,
  iconWidth: 24,
  iconColor: 'var(--color-black)',
};
export function PivotField(props: PivotFieldProps) {
  const [value, setValue] = React.useState(props?.value);
  const [defaultValue, setDefaultValue] = React.useState(props?.defaultValue);
  const [dropDownValue, setDropDownValue] = React.useState<BosDropDownOptions>(null);

  React.useEffect(() => {
    if (!props.value) return;
    setValue(props?.value);
  }, [props.value]);

  React.useEffect(() => {
    if (!props.defaultValue) return;
    setDefaultValue(props?.defaultValue);
  }, [props.defaultValue]);

  const getDropDownOptions = (): BosDropDownOptions[] => {
    if (props?.dropDownOptions?.some((x) => x?.isSelected)) return props?.dropDownOptions;

    return props?.dropDownOptions?.map((option, idx) => ({
      ...option,
      isSelected: option?.isSelected || idx === 0,
    }));
  };

  const onChangeDropDown = (selectedValue: BosDropDownOptions) => {
    setDropDownValue(selectedValue);
    if (typeof props.onChangeDropDown === 'function') {
      props.onChangeDropDown(selectedValue, value);
    }
  };

  const onChangeValue = (
    input: string | number,
    formatted?: string,
    event?: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    setDefaultValue('');
    setValue(formatted);
    if (typeof props.onChange === 'function') {
      props.onChange(input, dropDownValue, formatted, event);
    }
  };

  return (
    <div className={cn('flex flex-col gap-1 rounded-lg', props.label && 'items-baseline')}>
      {props.label && <div className="text-black text-base font-semibold leading-7">{props.label}</div>}
      <div
        className={cn(
          'h-fit grid grid-cols-[5fr_1fr] rounded-lg border border-neutralPlus',
          !props.disabled &&
            'bg-white group border border-neutralPlus hover:border-black focus-within:border-black hover:bg-lightGrey focus-within:bg-lightGrey',
          props.errorMessage && 'border-poppy hover:border-poppy focus-within:border-poppy',
          props.isValid && 'border-positive hover:border-positive focus-within:border-positive',
          props.disabled && 'border border-neutralGrey bg-lightGrey',
          props?.className
        )}
      >
        <div className="flex grow-1 shrink-0">
          <InputField
            id="inputField"
            disabled={props.disabled}
            defaultValue={defaultValue}
            value={value}
            placeholder={props.placeholder}
            type={props.type}
            onChange={onChangeValue}
            decimals={props.decimals}
            className="h-fit flex-1 shrink-0 border-y-0 border-l-0 rounded-r-none focus-visible:outline-none"
          />
        </div>
        <div className="flex">
          <Dropdown
            disabled={props.disabled}
            id={'inputDropDown'}
            onChange={((option: BosDropDownOptions | null) => option && onChangeDropDown(option)) as any}
            value={dropDownValue}
            options={getDropDownOptions()}
            placeholder=" "
            iconProps={dropDownIconProps}
            nextDropdown
            className="min-w-[5.5rem] text-lg border-none focus:outline-none group-hover:bg-lightGrey"
          />
        </div>
      </div>
      {!props?.isValid && props?.errorMessage && (
        <div className="w-full !h-1/2 flex justify-end">
          <MessageBar severity={TMessageBarSeverity.ERROR} className="!w-fit flex items-center tw-small py-1">
            {props.errorMessage}
          </MessageBar>
        </div>
      )}
    </div>
  );
}
